import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Show } from "shared/components";
import { SubmissionType } from "shared/types";
import { SubmissionDisclosureSubStep } from "../../constants";
import { useAuthContext } from "providers/Auth";
import { SelectMethodStep } from "./SelectMethodStep";
import { ReviewAndSendEmailStep } from "./ReviewAndSendEmailStep";
import { EmailSentStep } from "./EmailSentStep";
import { SignedStep } from "./SignedStep";
import { SubmissionDisclosureConfiguration } from "./types";
import { Dispatch, SetStateAction } from "react";
import { Application, Applicant, Verification } from "shared/types";
import { SignOnBehalfStep } from "./SignOnBehalf";

interface SubmissionDisclosureProps {
  refetchApplication: () => void;
  applicant: Applicant;
  application: Application;
  verification?: Verification;
  subStep: SubmissionDisclosureSubStep;
  setSubStep: Dispatch<SetStateAction<SubmissionDisclosureSubStep>>;
  onNextStep: () => void;
}

export const SubmissionDisclosure = ({
  refetchApplication,
  applicant,
  application,
  verification,
  subStep,
  setSubStep,
  onNextStep
}: SubmissionDisclosureProps) => {
  const { partner, pms } = useAuthContext();

  const [configuration, setConfiguration] =
    useState<SubmissionDisclosureConfiguration>({
      preferredSubmissionType: SubmissionType.SendByEmail,
      email: applicant.manual_email || ""
    });

  const submissionDisclosureStep = verification?.steps?.find(
    (step) => step.name === "submission_disclosure_step"
  );
  const description = (
    submissionDisclosureStep?.json_schema as { description: string }
  ).description;

  const configMutation = useMutation({
    mutationKey: [
      "MANUAL_APPLICATION_CONFIGURATION",
      applicant.id,
      application.id
    ],
    mutationFn: ({
      configuration
    }: {
      configuration: SubmissionDisclosureConfiguration;
    }) =>
      instance.patch(
        `/portal/v1/applications/${application.id}/applicants/${applicant.id}/update_preferred_manual_values`,
        {
          email: configuration.email,
          preferred_submission_disclosure_type:
            configuration.preferredSubmissionType
        }
      ),
    onSuccess: () => {
      refetchApplication();
      switch (configuration.preferredSubmissionType) {
        case SubmissionType.InPerson:
          setSubStep(SubmissionDisclosureSubStep.ReviewAndSign);
          break;
        case SubmissionType.SendByEmail:
          setSubStep(SubmissionDisclosureSubStep.ReviewAndSendEmail);
          break;
        case SubmissionType.SignOnBehalf:
          setSubStep(SubmissionDisclosureSubStep.SignOnBehalf);
          break;
      }
    }
  });

  const sendEmailMutation = useMutation({
    mutationKey: [
      "MANUAL_APPLICATION_SEND_EMAIL",
      applicant.id,
      application.id
    ],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${application.id}/applicants/${applicant.id}/send_submission_disclosure_email`
      ),
    onSuccess: () => {
      refetchApplication();
      setSubStep(SubmissionDisclosureSubStep.EmailSent);
      toast.success(
        "Submission disclosure email will be sent to the applicant."
      );
    }
  });

  const signOnBehalfMutation = useMutation({
    mutationKey: [
      "MANUAL_APPLICATION_SIGN_ON_BEHALF",
      applicant.id,
      application.id
    ],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${application.id}/applicants/${applicant.id}/sign_on_behalf_submission_disclosure`
      ),
    onSuccess: () => {
      refetchApplication();
      setSubStep(SubmissionDisclosureSubStep.Signed);
    }
  });

  const cancelEmailMutation = useMutation({
    mutationKey: [
      "MANUAL_APPLICATION_CANCEL_EMAIL",
      applicant.id,
      application.id
    ],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${application.id}/applicants/${applicant.id}/cancel_submission_disclosure_email`
      ),
    onSuccess: () => {
      refetchApplication();
      setSubStep(SubmissionDisclosureSubStep.SelectMethod);
      toast.success("Submission disclosure email will be canceled.");
    }
  });

  const handleSelectMethodContinue = () => {
    if (subStep === SubmissionDisclosureSubStep.SelectMethod)
      configMutation.mutate({
        configuration
      });
  };

  useEffect(() => {
    if (
      subStep === SubmissionDisclosureSubStep.EmailSent ||
      subStep === SubmissionDisclosureSubStep.SignOnBehalf
    )
      refetchApplication();
  }, [subStep]);

  return (
    <div className="h-[calc(80vh-72px)] flex flex-col">
      <Show if={subStep === SubmissionDisclosureSubStep.Signed}>
        <SignedStep onNext={onNextStep} />
      </Show>
      <Show if={subStep === SubmissionDisclosureSubStep.SelectMethod}>
        <SelectMethodStep
          configuration={configuration}
          setConfiguration={setConfiguration}
          onNext={handleSelectMethodContinue}
          isLoading={configMutation.isPending}
        />
      </Show>

      <Show if={subStep === SubmissionDisclosureSubStep.ReviewAndSendEmail}>
        <ReviewAndSendEmailStep
          description={description}
          partnerNameInSubmissionDisclosure={
            application.partner_name_in_submission_disclosure ||
            partner?.name ||
            "partner name"
          }
          isManageAmerica={pms.isManageAmerica}
          onSendEmail={sendEmailMutation.mutate}
          isLoading={sendEmailMutation.isPending}
        />
      </Show>
      <Show if={subStep === SubmissionDisclosureSubStep.SignOnBehalf}>
        <SignOnBehalfStep
          partnerNameInSubmissionDisclosure={
            application.partner_name_in_submission_disclosure ||
            partner?.name ||
            "partner name"
          }
          partner={partner}
          onSignOnBehalf={signOnBehalfMutation.mutate}
          isLoading={signOnBehalfMutation.isPending}
          applicantId={applicant.id}
          applicationId={application.id}
        />
      </Show>
      <Show if={subStep === SubmissionDisclosureSubStep.EmailSent}>
        <EmailSentStep
          isCancelLoading={cancelEmailMutation.isPaused}
          onCancel={cancelEmailMutation.mutate}
          onNext={onNextStep}
        />
      </Show>
    </div>
  );
};
